import { Injectable } from '@angular/core';
import { HomesFacade } from '@library/store/homes/homes.facade';
import { ModulesFacade } from '@library/store/modules/modules.facade';
import { Module } from '@library/store/modules/modules.interface';
import { UserFacade } from '@library/store/user/user.facade';
import { isDefined } from '@library/utils/helpers/store/is-defined';
import { ControlPageStore, ForecastData } from '@pages/control-page/control-page-store.service';
import { ControlPageService } from '@pages/control-page/control-page.service';
import { Enums } from 'constants/Enums.constant';
import { EMPTY, Subscription, switchMap, withLatestFrom } from 'rxjs';

@Injectable()
export class ForecastDataLoaderService {

  private sub = new Subscription();

  constructor(
    private homesFacade: HomesFacade,
    private modulesFacade: ModulesFacade,
    private userFacade: UserFacade,
    private controlPageService: ControlPageService,
    private controlPageStore: ControlPageStore,
  ) {}

  load() {
    this.sub.add(this.homesFacade.currentHomeId$.pipe(
      isDefined(),
      withLatestFrom(this.homesFacade.currentHome$, this.modulesFacade.currentHomeModules$, this.userFacade.currentUser$),
      switchMap(([, home, modules, {locale}]) => {
        const station = modules.find(m => m.type === Enums.ModuleType.NAMAIN);
        const outdoorModule = modules.find(m => m.type === Enums.ModuleType.NAMODULE1);

        // By default we use the station for the forecast
        let module: Module = station;

        /**
         * If the home is a favorite we need to use the outdoormodule for forecast data,
         * Otherwise the api will return a http error 404
         */
        if (home.mode === 'favorite') {
          module = outdoorModule;
        }

        if (module) {
          return this.controlPageService.getForecastData({
            device_id: module.bridge ?? module.id,
            module_id: module.id,
            locale,
            ask_graphs: ['rain', 'wind', 'temperature', 'rain_proba', 'humidity', 'symbol'],
          });
        }
        else {
          return EMPTY;
        }
      })
    ).subscribe(forecast => {
      this.controlPageStore.setForecastData({forecast: this.adaptMaxMinForecast(forecast)});
    }));
  }

  adaptMaxMinForecast(forecast: ForecastData){
    const forecastDays = forecast.forecastDays?.map(dayF => {
      const endDay = dayF.day_ts + 86400;
      let temperatureValues = forecast.forecastGraphs.temperature.filter(v => dayF.day_ts <= v[0] && v[0] < endDay).map(v => v[1]);
      temperatureValues = temperatureValues.filter(v => v !== null);
      return {
        ...dayF,
        min_temp: Math.min(...temperatureValues),
        max_temp: Math.max(...temperatureValues),
      };
    }) ?? [];

    return {
      ...forecast,
      forecastDays
    };
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
  }

}
